import React, {useState} from 'react';
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {Alert, Box, Button, CircularProgress, Grid, IconButton, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Edit} from '@mui/icons-material';
import {useQuery} from '@apollo/client';
import {SERVICES_QUERY} from '../../gql/appointments.gql';
import {useStaffManagementAPI} from '../../service/useStaffManagementAPI';
import StaffEditModal from './StaffEditModal';
import Loading from '../../components/common/Loading';
import {useTranslation} from 'react-i18next';
import AppSnackbar from "../../common/AppSnackbar";

function StaffList() {
    const {t} = useTranslation();
    const {listStaff, setStaffActive} = useStaffManagementAPI();

    const [staff, setStaff] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const {data: servicesData, loading: servicesLoading} = useQuery(SERVICES_QUERY, {variables: {active: null}});
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [severity, setSeverity] = useState("success");
    const [modalOpen, setModalOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);

    const fetchStaff = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const staffs = await listStaff();
            setStaff(Array.isArray(staffs) ? staffs : []);
        } catch (e) {
            setError(t('employeesLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [listStaff, t]);

    React.useEffect(() => {
        fetchStaff().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staff.length]);

    const onToggleActive = async (row, next) => {
        const prev = staff;
        setStaff((arr) => arr.map((s) => (s.uuid === row.uuid ? {...s, active: next} : s)));
        try {
            await setStaffActive(row.uuid, next)
                .then(() => fetchStaff());
        } catch (e) {
            setStaff(prev);
            setSnackbarMessage(t('toggleActiveFailed'));
            setSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const openNew = () => {
        setEditing(null);
        setModalOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setModalOpen(true);
    };

    const onSaved = async () => {
        try {
            await fetchStaff();
            setSnackbarMessage(t('staffSaved'));
            setSeverity('success');
            setOpenSnackbar(true);
        } catch (_e) {
            // ignore
        }
    };

    const noActive = !loading && staff.filter((s) => s.active).length === 0;

    return (
        <Box sx={{width: '100%'}}>
            <Grid container spacing={2} alignItems="center" sx={{mb: 2}}>
                <Grid item xs={12} sm sx={{textAlign: {xs: 'left', sm: 'right'}}}>
                    <Button variant="contained" onClick={openNew} data-testid="add-staff-button">+ {t('newStaff')}</Button>
                </Grid>
            </Grid>

            {noActive && (
                <Box sx={{mb: 2}}>
                    <Alert severity="warning">{t('noActiveStaffWarning')}</Alert>
                </Box>
            )}

            <Paper variant="outlined">
                {loading && (
                    <Box sx={{p: 3, display: 'flex', gap: 1, alignItems: 'center'}}>
                        <CircularProgress size={20}/>
                        <Typography>{t('loadingEmployees')}</Typography>
                    </Box>
                )}
                {!loading && error && (
                    <Box sx={{p: 2}}>
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchStaff}>{t('retry')}</Button>}>
                            {error}
                        </Alert>
                    </Box>
                )}
                {!loading && !error && (
                    <Table size="small" aria-label={t('employeesTableAria')}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('name')}</TableCell>
                                <TableCell>{t('services')}</TableCell>
                                <TableCell>{t('active')}</TableCell>
                                <TableCell>{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {staff.map((row) => {
                                const fullName = row?.name || `${row?.person?.firstName || ''} ${row?.person?.lastName || ''}`.trim();
                                return (
                                    <TableRow key={row.uuid} hover>
                                        <TableCell>{fullName || '-'}</TableCell>
                                        <TableCell>{servicesLoading ? t('loading') : row.numberOfServices}</TableCell>
                                        <TableCell>
                                            <Switch
                                                size="small"
                                                checked={!!row.active}
                                                onChange={(e) => onToggleActive(row, e.target.checked)}
                                                inputProps={{'aria-label': t('toggleActiveFor', {name: fullName || t('employee'), state: row.active ? t('off') : t('on')}), 'data-testid': `toggle-staff-active-${fullName}`}}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" aria-label={t('edit')} onClick={() => openEdit(row)} data-testid={`edit-staff-button-${fullName}`}>
                                                <Edit fontSize="inherit"/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {staff.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography variant="body2" sx={{p: 2}}>{t('noStaffFound')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Create/Edit Modal */}
            <StaffEditModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditing(null);
                }}
                initial={editing}
                onSaved={onSaved}
            />

            <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar} severity={severity} />
        </Box>
    );
}

export default withAuthenticationRequired(StaffList, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login"
});
